import { TMasterLanguageTest } from "@/api/entities/master-language-test.entity";
import { TWorkExperience } from "@/api/entities/talent.entity";
import { TCreateTalentReqDto } from "@/api/services/main/talent/dtos/create-talent-req.dto";
import { TGetTalentDto } from "@/api/services/main/talent/dtos/get-talent.dto";
import { TUpdateTalentReqDto } from "@/api/services/main/talent/dtos/update-talent-req.dto";
import { DATE_FORMAT_FOR_API, MONTH_YEAR_FOR_API } from "@/constants/datetime";
import { TTalentForm, TTalentUploadFile } from "@/features/talent/types/talentForm";
import { calculateSpecificSkillTransitionPeriod } from "@/features/talent/utils/calculateSpecificSkillTransitionPeriod";
import { dayjs } from "@/utils/dayjs";
import { convertNumberToZipCode } from "@/utils/input";
import { normalizeString } from "@/utils/string";
import { isCertificateHiddenForIndustry } from "@/features/talent/utils/is-certificate-valid-for-industry";
import { TMasterSpecificSkillCertificate } from "@/api/entities/master-specific-skill-certificate.entity";

const cleanArray = <T>(arr: (T | null | undefined)[] | null | undefined): T[] | null => {
  if (!arr) return null;

  const cleaned = arr.filter((item): item is T => {
    if (item == null) return false;
    if (typeof item === "string") return item.trim() !== "";
    if (typeof item === "object") return Object.keys(item as object).some((k) => (item as Record<string, unknown>)[k] != null);
    return true;
  });

  return cleaned.length > 0 ? cleaned : null;
};

const isWorkExperienceEmpty = (exp: TWorkExperience): boolean => {
  return (
    (!exp.jobType || exp.jobType === "") &&
    (!exp.companyName || exp.companyName === "") &&
    (!exp.startDate || exp.startDate === null) &&
    (!exp.endDate || exp.endDate === null)
  );
};

export const convertTalentFormDataReq = (
  data: TTalentForm,
  languageTestData: TMasterLanguageTest[],
  specificSkillCertificateData: TMasterSpecificSkillCertificate[],
): TCreateTalentReqDto | TUpdateTalentReqDto => {
  // Certificates hidden for the talent's 分野 are not shown in the dropdown, so a
  // stale id can sit in form state with no visible chip and be resent on every
  // save. Drop those to break that loop — but only those: a certificate with no
  // mapping row is legitimate user-entered data and must survive, matching the
  // server's write-path guard.
  // Without a 分野 the server persists no certificates at all, so send none.
  const validSpecificSkillCertificates = data.companySpecificIndustryId
    ? data.specificSkillCertificates?.filter(
        (certificateId) =>
          !specificSkillCertificateData.some(
            (certificate) =>
              Number(certificate.id) === Number(certificateId) &&
              isCertificateHiddenForIndustry(certificate, data.companySpecificIndustryId),
          ),
      )
    : null;

  const cleanedWorkExperiences = cleanArray(
    data.workExperiences
      ?.filter((exp) => !isWorkExperienceEmpty(exp))
      .map((exp) => ({
        id: exp.id ?? undefined,
        jobType: normalizeString(exp.jobType),
        companyName: normalizeString(exp.companyName),
        countryId: exp.countryId ?? null,
        startDate: exp.startDate ? dayjs(exp.startDate).format(MONTH_YEAR_FOR_API) : null,
        endDate: exp.endDate ? dayjs(exp.endDate).format(MONTH_YEAR_FOR_API) : null,
      })),
  );

  const cleanedLanguageTestLevelIds = cleanArray(data?.languageTestLevelIds);

  const languageCertificates: Array<{ languageTestId: number; languageTestLevelId?: number | null }> = [];

  if (cleanedLanguageTestLevelIds && cleanedLanguageTestLevelIds.length > 0) {
    cleanedLanguageTestLevelIds.forEach((id) => {
      if (id === null || id === undefined) return;

      if (id < 0) {
        // Negative value represents test without levels
        const testId = -id;
        languageCertificates.push({
          languageTestId: testId,
          languageTestLevelId: null,
        });
      } else {
        // Positive value represents level id
        const test = languageTestData.find((t) => t.levels.some((level) => level.id.toString() === id.toString()));
        if (test) {
          languageCertificates.push({
            languageTestId: test.id,
            languageTestLevelId: id,
          });
        }
      }
    });
  }

  let zipCode = undefined;

  // BE may return zipCode as either string ("150-0001") or number (1500001).
  // Coerce to string before strip — defensive after IROHANA_MATCH_DEV-281
  // routed talent-detail data (where zipCode often arrives as number) into
  // this converter via the scan modal update flow.
  if (data.zipCode) {
    zipCode = String(data.zipCode).replaceAll("-", "");
  }

  const raw = {
    recruitmentStatus: data.recruitmentStatus,
    isRecommendedTalent: data.isRecommendedTalent ?? false,
    fullName: normalizeString(data.fullName),
    fullNameKana: normalizeString(data.fullNameKana),
    birthday: data.birthday ? dayjs(data.birthday).format(DATE_FORMAT_FOR_API) : null,
    nickName: normalizeString(data.nickName),
    talentNumber: normalizeString(data.talentNumber),
    nationalityId: data.nationalityId ?? undefined,
    gender: data.gender ?? undefined,
    phoneNumber: data.phoneNumber ? (normalizeString(data.phoneNumber) ?? null) : null,
    email: normalizeString(data.email),
    location: data.location ?? undefined,
    religion: normalizeString(data.religion),
    zipCode: zipCode,
    prefecture: normalizeString(data.prefecture),
    city: normalizeString(data.city),
    address: normalizeString(data.address),
    country: normalizeString(data.country),
    avatarFile: data.avatarFile ?? null,
    avatarFilePath: data.avatarFilePath ?? null,
    passportNumber: normalizeString(data.passportNumber),
    passportExpiryDate: data.passportExpiryDate ? dayjs(data.passportExpiryDate).format(DATE_FORMAT_FOR_API) : null,
    cardNumber: normalizeString(data.cardNumber),
    residenceStatusId: data.residenceStatusId,
    periodOfStay: data.periodOfStay ? dayjs(data.periodOfStay).format(DATE_FORMAT_FOR_API) : null,
    specifiedSkillFirstStartDate: data.specifiedSkillFirstStartDate ? dayjs(data.specifiedSkillFirstStartDate).format(DATE_FORMAT_FOR_API) : null,
    blankPeriodMonthly: normalizeString(data.blankPeriodMonthly),
    isBeforeEntry: !!data.isBeforeEntry,
    languageCertificates: languageCertificates.length > 0 ? languageCertificates : null,
    languageTestOther: normalizeString(data.languageTestOther),
    companySpecificIndustryId: data.companySpecificIndustryId ?? null,
    specificBusinessClassificationIds: data.specificBusinessClassificationIds ?? null,
    specificSkillCertificates: cleanArray(validSpecificSkillCertificates),
    languageSkills: cleanArray(
      data.languageSkills?.map((skill) => ({
        ...skill,
        content: normalizeString(skill?.content),
      })),
    ),
    workExperiences: cleanedWorkExperiences,
    educationBackground: cleanArray(
      data.educationBackground?.map((edu) => ({
        id: edu.id ?? undefined,
        educationLevelId: edu.educationLevelId ?? null,
        schoolName: normalizeString(edu.schoolName),
        countryId: edu.countryId ?? null,
        enrollmentDate: edu.enrollmentDate ? dayjs(edu.enrollmentDate).format(DATE_FORMAT_FOR_API) : null,
        graduationDate: edu.graduationDate ? dayjs(edu.graduationDate).format(DATE_FORMAT_FOR_API) : null,
      })),
    ),
    heldQualifications: cleanArray(data.heldQualifications?.map((qual) => normalizeString(qual))),
    memo: normalizeString(data.memo),
    interviewEvaluation: normalizeString(data.interviewEvaluation),
    familyInformation: normalizeString(data.familyInformation),
    majorId: data.majorId ?? null,
    jobTypeId: data.jobTypeId ?? null,
    desiredJobTypeIds: data.desiredJobTypeIds ?? null,
    desiredWorkLocation: normalizeString(data.desiredWorkLocation),
    expectSalary: normalizeString(data.expectSalary),
    availableFrom: normalizeString(data.availableFrom),
    otherRequest: normalizeString(data.otherRequest),
    internalMemo: normalizeString(data.internalMemo),
    interviewDate: data.interviewDate ? dayjs(data.interviewDate).format(DATE_FORMAT_FOR_API) : null,
    interviewerId: data.interviewerId ?? null,
    partnerId: data.partnerId ?? null,
    personInChargeId: data.personInChargeId,
    matchOfficeId: data.matchOfficeId ?? null,
    relatedFiles: data.relatedFiles ?? null,
    relatedFilePaths: JSON.stringify(data.relatedFilePaths) ?? null,
    relatedFileTypes: JSON.stringify(data.relatedFileTypes) ?? null,
    contactMethods: cleanArray(
      data.contactMethods?.map((cm) => ({
        id: cm.id ?? undefined,
        contactType: cm.contactType ?? null,
        contactInfo: normalizeString(cm.contactInfo),
      })),
    ),
  };

  const payload = raw as TCreateTalentReqDto;
  return payload;
};

export const convertResToFormData = (data: TGetTalentDto): Partial<TTalentForm> | undefined => {
  if (!data) return undefined;

  const specificSkillCertificatesStr = data?.specificSkillCertificates
    ?.map((item) => item?.specificSkillCertificate?.certificateName)
    .filter(Boolean)
    .join("、");

  const languageTestLevelIdsStr = data?.languageCertificates
    ?.map((cert) => {
      if (cert?.languageTestLevel?.level) {
        return cert.languageTestLevel.level;
      }
      return null;
    })
    .filter(Boolean)
    .join("、");

  let specifiedSkillFirstRemainingTermYear: number | undefined;
  let specifiedSkillFirstRemainingTermMonth: number | undefined;
  let specifiedSkillFirstRemainingTerm: string | undefined;

  if (data?.isBeforeEntry) {
    specifiedSkillFirstRemainingTermYear = 5;
    specifiedSkillFirstRemainingTermMonth = 0;
    specifiedSkillFirstRemainingTerm = "5/0";
  } else if (data?.specifiedSkillFirstStartDate) {
    const { yearPeriod, monthPeriod } = calculateSpecificSkillTransitionPeriod({
      specifiedSkillFirstStartDate: dayjs(data.specifiedSkillFirstStartDate),
      blankPeriodMonthly: data.blankPeriodMonthly?.toString(),
    });
    specifiedSkillFirstRemainingTermYear = yearPeriod;
    specifiedSkillFirstRemainingTermMonth = monthPeriod;
    specifiedSkillFirstRemainingTerm = `${yearPeriod}/${monthPeriod}`;
  }

  const filteredWorkExperiences = data?.workExperiences?.filter((exp) => !isWorkExperienceEmpty(exp)) || [];
  const parsedWorkExperiences =
    filteredWorkExperiences.length > 0
      ? filteredWorkExperiences.map((exp) => ({
          ...exp,
          startDate: exp.startDate ? dayjs(exp.startDate) : null,
          endDate: exp.endDate ? dayjs(exp.endDate) : null,
        }))
      : undefined;

  const dataTestLevels: (number | null)[] = [];
  if (data?.languageCertificates && data.languageCertificates.length > 0) {
    data.languageCertificates.forEach((cert) => {
      if (cert?.languageTestLevel?.id) {
        dataTestLevels.push(Number(cert.languageTestLevel.id));
      } else if (cert?.languageTestId) {
        dataTestLevels.push(-Number(cert.languageTestId));
      }
    });
  }
  const dataTestLevelIds = dataTestLevels.length > 0 ? dataTestLevels : null;

  let talentUploadFiles: TTalentUploadFile[] = [];
  if (data?.talentUploadFiles?.length) {
    talentUploadFiles = [...data?.talentUploadFiles].sort((a, b) => Number(a.id) - Number(b.id));
  }

  let zipCode = undefined;

  if (data.zipCode) {
    zipCode = convertNumberToZipCode(data.zipCode);
  }

  const response = {
    id: data?.id,
    recruitmentStatus: data.recruitmentStatus,
    isRecommendedTalent: data.isRecommendedTalent,
    fullName: data.fullName,
    fullNameKana: data.fullNameKana,
    birthday: data.birthday ? dayjs(data.birthday) : null,
    nickName: data.nickName,
    talentNumber: data.talentNumber,
    nationalityId: data?.nationality ? Number(data?.nationality?.id) : undefined,
    nationality: data.nationality,
    specificBusinessClassifications: data.specificBusinessClassifications ?? [],
    specificBusinessClassificationIds: data.specificBusinessClassifications?.map((item) => item.id) ?? null,
    companySpecificIndustry: data.companySpecificIndustry,
    companySpecificIndustryId: data.companySpecificIndustryId ?? data.companySpecificIndustry?.id ?? null,
    languageCertificates: data.languageCertificates ?? [],
    gender: data.gender,
    phoneNumber: data.phoneNumber,
    email: data.email,
    religion: data.religion,
    zipCode: zipCode,
    prefecture: data.prefecture,
    city: data.city,
    address: data.address,
    country: data.country,
    avatarFilePath: data.avatarFilePath,
    passportNumber: data.passportNumber,
    passportExpiryDate: data.passportExpiryDate ? dayjs(data.passportExpiryDate) : null,
    cardNumber: data.cardNumber,
    residenceStatusId: data?.residenceStatus?.id,
    residenceStatus: data.residenceStatus,
    periodOfStay: data.periodOfStay ? dayjs(data.periodOfStay) : null,
    specifiedSkillFirstStartDate: data.specifiedSkillFirstStartDate ? dayjs(data.specifiedSkillFirstStartDate) : null,
    isBeforeEntry: !!data.isBeforeEntry,
    specifiedSkillFirstRemainingTerm: specifiedSkillFirstRemainingTerm,
    languageTestLevelIds: dataTestLevelIds,
    languageTestLevelIdsStr: languageTestLevelIdsStr ?? null,
    languageTestOther: data.languageTestOther,
    jobTypeId: data?.jobType?.id,
    specificSkillCertificates: data?.specificSkillCertificates?.map((item) => item?.specificSkillCertificate?.id),
    specificSkillCertificateStr: specificSkillCertificatesStr,
    languageSkills: data.languageSkills ?? null,
    workExperiences: parsedWorkExperiences,
    educationBackground:
      data.educationBackgrounds && data.educationBackgrounds.length > 0
        ? data.educationBackgrounds.map((edu) => ({
            id: edu.id ?? null,
            educationLevelId: edu.educationLevelId ?? null,
            schoolName: edu.schoolName ?? null,
            countryId: edu.countryId ?? null,
            countryName: edu.countryName ?? null,
            enrollmentDate: edu.enrollmentDate ? dayjs(edu.enrollmentDate) : null,
            graduationDate: edu.graduationDate ? dayjs(edu.graduationDate) : null,
          }))
        : undefined,
    heldQualifications: data.heldQualifications,
    memo: data.memo,
    interviewEvaluation: data.interviewEvaluation,
    familyInformation: data.familyInformation,
    majorId: data?.major?.id,
    major: data.major,
    desiredJobTypeIds: data?.desiredJobTypes?.map((item) => Number(item.id)) ?? null,
    desiredJobTypes: data.desiredJobTypes,
    jobType: data.jobType,
    location: data.location,
    desiredWorkLocation: data.desiredWorkLocation,
    expectSalary: data.expectSalary,
    blankPeriodMonthly: data.blankPeriodMonthly,
    availableFrom: data.availableFrom,
    otherRequest: data.otherRequest,
    internalMemo: data.internalMemo,
    interviewDate: data.interviewDate ? dayjs(data.interviewDate) : null,
    interviewer: data.interviewer,
    interviewerId: data.interviewerId,
    partner: data.partner,
    partnerId: data.partner?.id,
    personInCharge: data.personInCharge,
    personInChargeId: data.personInCharge?.id,
    matchOfficeId: (data.matchOffice?.id ?? data.matchOfficeId) as number,
    matchOffice: data.matchOffice,
    talentUploadFiles: talentUploadFiles,
    specifiedSkillFirstRemainingTermMonth: specifiedSkillFirstRemainingTermMonth,
    specifiedSkillFirstRemainingTermYear: specifiedSkillFirstRemainingTermYear,
    caseMatches: data.caseMatches,
    contactMethods: data.contactMethods?.length
      ? data.contactMethods.map((cm) => ({
          id: cm.id,
          contactType: cm.contactType ?? null,
          contactInfo: cm.contactInfo ?? null,
        }))
      : [{ contactType: null, contactInfo: null }],
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };

  return response;
};

const normalizeEmptyValue = (value: unknown): unknown => {
  if (value === null || value === "" || value === undefined) {
    return null;
  }
  return value;
};

export const isValueDifferent = (current: unknown, initial: unknown): boolean => {
  const normalizedCurrent = normalizeEmptyValue(current);
  const normalizedInitial = normalizeEmptyValue(initial);

  if (normalizedCurrent === normalizedInitial) return false;
  if (normalizedCurrent == null && normalizedInitial == null) return false;
  if (normalizedCurrent == null || normalizedInitial == null) return true;
  if (typeof normalizedCurrent !== typeof normalizedInitial) return true;
  if (Array.isArray(normalizedCurrent) && Array.isArray(normalizedInitial)) {
    if (normalizedCurrent.length !== normalizedInitial.length) return true;
    for (let i = 0; i < normalizedCurrent.length; i++) {
      if (isValueDifferent(normalizedCurrent[i], normalizedInitial[i])) return true;
    }
    return false;
  }
  if (typeof normalizedCurrent === "object" && typeof normalizedInitial === "object") {
    const currentKeys = Object.keys(normalizedCurrent as Record<string, unknown>);
    const initialKeys = Object.keys(normalizedInitial as Record<string, unknown>);
    if (currentKeys.length !== initialKeys.length) return true;
    for (const key of currentKeys) {
      if (!initialKeys.includes(key)) return true;
      if (isValueDifferent((normalizedCurrent as Record<string, unknown>)[key], (normalizedInitial as Record<string, unknown>)[key])) return true;
    }
    return false;
  }
  return normalizedCurrent !== normalizedInitial;
};

export const isDefaultValuesChanged = (prev: Partial<TTalentForm> | undefined, current: Partial<TTalentForm> | undefined): boolean => {
  if (prev === current) return false;
  if (prev == null && current == null) return false;
  if (prev == null || current == null) return true;
  return isValueDifferent(prev, current);
};

const isEmptyArrayValue = (value: unknown[]): boolean => {
  if (value.length === 0) return true;
  return value.every((item) => {
    if (item === null || item === undefined) return true;
    if (typeof item === "string" && item.trim() === "") return true;
    if (typeof item === "object" && !Array.isArray(item)) {
      const keys = Object.keys(item);
      if (keys.length === 0) return true;
      return keys.every((key) => {
        const objValue = (item as Record<string, unknown>)[key];
        return isEmptyValue(objValue);
      });
    }
    return false;
  });
};

const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") return true;
  if (typeof value === "boolean") return false;
  if (typeof value === "number") {
    return value === 0;
  }

  if (typeof value === "object" && value !== null && "isValid" in value && typeof (value as { isValid?: () => boolean }).isValid === "function") {
    const dayjsObj = value as { isValid?: () => boolean };
    return !dayjsObj.isValid || !dayjsObj.isValid();
  }
  if (Array.isArray(value)) {
    return isEmptyArrayValue(value);
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    const keys = Object.keys(value);
    if (keys.length === 0) return true;
    return keys.every((key) => {
      const objValue = (value as Record<string, unknown>)[key];
      return isEmptyValue(objValue);
    });
  }
  return false;
};

export const isFormDataEmpty = (data: Partial<TTalentForm>): boolean => {
  const fieldsToCheck = Object.entries(data).filter(([key]) => {
    return !["recruitmentStatus", "id", "isRecommendedTalent"].includes(key);
  });

  if (fieldsToCheck.length === 0) return true;

  return fieldsToCheck.every(([_, value]) => isEmptyValue(value));
};
